import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Loader2, ImagePlus } from "lucide-react";
import { createVehicle, updateVehicle, uploadImage, formatApiErrorDetail, mediaUrl } from "@/api";

const EMPTY = {
  make: "", model: "", year: new Date().getFullYear(), price: "", day_rate: "",
  mileage_km: "", transmission: "Manual", fuel: "Petrol",
  services: ["rent-to-own"], status: "available", image: "", images: [], blurb: "",
};

const STATUS_OPTIONS = [
  { value: "available", label: "Available" },
  { value: "reserved", label: "Reserved" },
  { value: "on-rent-to-own", label: "On Rent-to-Own" },
  { value: "sold", label: "Sold" },
];

const SERVICES = [
  { key: "rent-to-own", label: "Rent-to-Own" },
  { key: "rental", label: "Rental" },
  { key: "sale", label: "Sale" },
];

export default function VehicleFormModal({ vehicle, onClose, onSaved }) {
  const isEdit = Boolean(vehicle);
  const [form, setForm] = useState(isEdit ? { ...EMPTY, ...vehicle } : EMPTY);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadImage(file);
      setForm((f) => ({ ...f, image: res.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleExtraFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadImage(file);
      setForm((f) => ({ ...f, images: [...(f.images || []), res.url] }));
      toast.success("Photo added to gallery");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleService = (key) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(key)
        ? f.services.filter((s) => s !== key)
        : [...f.services, key],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.services.length === 0) return toast.error("Select at least one service type.");
    setLoading(true);
    const payload = {
      ...form,
      year: Number(form.year),
      price: Number(form.price),
      day_rate: Number(form.day_rate),
      mileage_km: Number(form.mileage_km || 0),
    };
    try {
      if (isEdit) {
        await updateVehicle(vehicle.id, payload);
        toast.success("Vehicle updated");
      } else {
        await createVehicle(payload);
        toast.success("Vehicle added to stock");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="vehicle-form-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-[#081738]/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-2xl bg-[#122659] border border-sky-300/20 rounded-xl p-8 my-8"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl font-black text-white">
              {isEdit ? "Edit vehicle" : "Add vehicle"}
            </h2>
            <button data-testid="vf-close-btn" onClick={onClose} className="text-slate-400 hover:text-white transition-colors" aria-label="Close">
              <X size={22} />
            </button>
          </div>

          <form onSubmit={submit} data-testid="vehicle-form" className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="input-label" htmlFor="vf-make">Make *</label>
              <input id="vf-make" data-testid="vf-input-make" required className="input-field" value={form.make} onChange={set("make")} placeholder="Toyota" />
            </div>
            <div>
              <label className="input-label" htmlFor="vf-model">Model *</label>
              <input id="vf-model" data-testid="vf-input-model" required className="input-field" value={form.model} onChange={set("model")} placeholder="Corolla Quest 1.8" />
            </div>
            <div>
              <label className="input-label" htmlFor="vf-year">Year *</label>
              <input id="vf-year" data-testid="vf-input-year" type="number" required className="input-field" value={form.year} onChange={set("year")} />
            </div>
            <div>
              <label className="input-label" htmlFor="vf-mileage">Mileage (km)</label>
              <input id="vf-mileage" data-testid="vf-input-mileage" type="number" min="0" className="input-field" value={form.mileage_km} onChange={set("mileage_km")} placeholder="45000" />
            </div>
            <div>
              <label className="input-label" htmlFor="vf-price">Price (R) *</label>
              <input id="vf-price" data-testid="vf-input-price" type="number" min="0" required className="input-field" value={form.price} onChange={set("price")} placeholder="189900" />
            </div>
            <div>
              <label className="input-label" htmlFor="vf-dayrate">From rate (R/day) *</label>
              <input id="vf-dayrate" data-testid="vf-input-dayrate" type="number" min="0" required className="input-field" value={form.day_rate} onChange={set("day_rate")} placeholder="109" />
            </div>
            <div>
              <label className="input-label" htmlFor="vf-trans">Transmission</label>
              <select id="vf-trans" data-testid="vf-select-transmission" className="input-field" value={form.transmission} onChange={set("transmission")}>
                <option>Manual</option>
                <option>Automatic</option>
              </select>
            </div>
            <div>
              <label className="input-label" htmlFor="vf-fuel">Fuel</label>
              <select id="vf-fuel" data-testid="vf-select-fuel" className="input-field" value={form.fuel} onChange={set("fuel")}>
                <option>Petrol</option>
                <option>Diesel</option>
                <option>Hybrid</option>
              </select>
            </div>
            <div>
              <label className="input-label" htmlFor="vf-status">Stock status</label>
              <select id="vf-status" data-testid="vf-select-status" className="input-field" value={form.status} onChange={set("status")}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <span className="input-label">Available for *</span>
              <div className="flex flex-wrap gap-3" data-testid="vf-services">
                {SERVICES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    data-testid={`vf-service-${s.key}`}
                    onClick={() => toggleService(s.key)}
                    className={`text-sm font-bold px-4 py-2.5 rounded-md border transition-colors duration-200 ${
                      form.services.includes(s.key)
                        ? "bg-[#F57C00] border-[#F57C00] text-white"
                        : "border-sky-300/30 text-slate-300 hover:border-sky-300"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <span className="input-label">Photo</span>
              {form.image && (
                <img src={mediaUrl(form.image)} alt="Vehicle preview" data-testid="vf-photo-preview" className="w-full h-40 object-cover rounded-md border border-sky-300/20 mb-3" />
              )}
              <label
                data-testid="vf-upload-label"
                className={`flex items-center justify-center gap-2 border-2 border-dashed border-sky-300/30 rounded-md px-4 py-4 text-sm font-bold cursor-pointer transition-colors duration-200 ${
                  uploading ? "text-slate-500" : "text-sky-300 hover:border-sky-300 hover:text-white"
                }`}
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                {uploading ? "Uploading…" : "Upload a photo from your device"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  data-testid="vf-upload-input"
                  className="hidden"
                  onChange={handleFile}
                  disabled={uploading}
                />
              </label>
              <label
                data-testid="vf-upload-extra-label"
                className={`mt-2 flex items-center justify-center gap-2 border border-sky-300/20 rounded-md px-4 py-3 text-xs font-bold cursor-pointer transition-colors duration-200 ${
                  uploading ? "text-slate-500" : "text-slate-400 hover:border-sky-300 hover:text-white"
                }`}
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                {uploading ? "Uploading…" : "Add another photo to the gallery"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  data-testid="vf-upload-extra-input"
                  className="hidden"
                  onChange={handleExtraFile}
                  disabled={uploading}
                />
              </label>
              {(form.images || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="vf-gallery">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative group/thumb">
                      <img src={mediaUrl(img)} alt="" className="w-20 h-14 object-cover rounded-md border border-sky-300/20" />
                      <button
                        type="button"
                        data-testid={`vf-gallery-remove-${i}`}
                        onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, x) => x !== i) }))}
                        className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs opacity-0 group-hover/thumb:opacity-100 transition-opacity"
                        aria-label="Remove photo"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input id="vf-image" data-testid="vf-input-image" type="url" className="input-field mt-3" value={form.image} onChange={set("image")} placeholder="…or paste an image URL" />
            </div>
            <div className="sm:col-span-2">
              <label className="input-label" htmlFor="vf-blurb">Short description</label>
              <textarea id="vf-blurb" data-testid="vf-input-blurb" rows={2} className="input-field resize-none" value={form.blurb} onChange={set("blurb")} placeholder="One line that sells this car" />
            </div>
            <button
              type="submit"
              data-testid="vf-submit-btn"
              disabled={loading}
              className="sm:col-span-2 bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white font-bold px-6 py-4 rounded-md transition-colors duration-300 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {loading ? "Saving…" : isEdit ? "Save changes" : "Add vehicle"}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
